import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  useGetTenantInformationQuery,
  useUpdateTenantShopDeliveryMutation,
  useTestShopDeliveryWebhookMutation,
} from "src/features/tenants/tenantsApiSlice";
import Loading from "src/components/spinners/Loading";
import TextField from "src/components/form/fields/TextField";

const ShopDeliverySettings = () => {
  const {
    data: tenant,
    refetch,
    isLoading,
    isFetching,
  } = useGetTenantInformationQuery();
  const [updateShopDelivery, { isLoading: isSaving }] =
    useUpdateTenantShopDeliveryMutation();
  const [testWebhook, { isLoading: isTesting }] =
    useTestShopDeliveryWebhookMutation();

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const cfg = tenant?.settings?.shopDelivery;

  const defaultValues = useMemo(
    () => ({
      enabled: cfg?.enabled || false,
      shippingDashboardUrl: cfg?.shippingDashboardUrl || "",
      webhookSecret: cfg?.webhookSecret || "",
      defaultFulfillmentType: cfg?.defaultFulfillmentType || "shipping",
      autoNotifyOnCreate: cfg?.autoNotifyOnCreate !== false,
    }),
    [cfg],
  );

  const { handleSubmit, control, reset } = useForm({ defaultValues });

  const [enabled, dashboardUrl, webhookSecret] = useWatch({
    control,
    name: ["enabled", "shippingDashboardUrl", "webhookSecret"],
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = async (data) => {
    setError(null);
    setSuccess(false);
    setTestResult(null);
    try {
      await updateShopDelivery(data).unwrap();
      setSuccess(true);
      refetch();
    } catch (err) {
      setError(
        err?.data?.message || "Fehler beim Speichern der Versand-Einstellungen",
      );
    }
  };

  const handleTestConnection = async () => {
    setTestResult(null);
    try {
      const result = await testWebhook().unwrap();
      setTestResult({ type: result.type || "success", message: result.message });
    } catch (err) {
      setTestResult({
        type: "error",
        message: err?.data?.message || "Verbindungstest fehlgeschlagen",
      });
    }
  };

  if (isLoading || isFetching) {
    return <Loading />;
  }

  const canTest = Boolean(
    String(dashboardUrl || "").trim() && String(webhookSecret || "").trim(),
  );

  return (
    <>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Shop-Versand
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Typography sx={{ my: 2 }} variant="body1">
        Konfigurieren Sie die Anbindung an das Versand-Dashboard, um
        Bestellungen automatisch an die Versandabwicklung zu senden.
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Enable toggle */}
          <Grid item xs={12}>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Versand-Integration aktivieren"
                />
              )}
            />
          </Grid>

          {/* Dashboard URL */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="shippingDashboardUrl"
              label="Versand-Dashboard URL"
              control={control}
              placeholder="https://versand.example.com"
              helperText="Die URL des Versand-Dashboards (z.B. https://versand.example.com)"
              disabled={!enabled}
            />
          </Grid>

          {/* Webhook Secret */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="webhookSecret"
              label="Webhook-Secret"
              control={control}
              helperText="Gemeinsames Secret zur HMAC-Signierung der Webhooks. Muss im Versand-Dashboard identisch konfiguriert sein."
              disabled={!enabled}
            />
          </Grid>

          {/* Default fulfillment type */}
          <Grid item xs={12} md={6}>
            <Controller
              name="defaultFulfillmentType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth disabled={!enabled}>
                  <InputLabel>Standard-Versandart</InputLabel>
                  <Select {...field} label="Standard-Versandart">
                    <MenuItem value="shipping">Versand</MenuItem>
                    <MenuItem value="local">Vor-Ort-Ausgabe</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Auto-notify toggle */}
          <Grid item xs={12} md={6}>
            <Controller
              name="autoNotifyOnCreate"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={!enabled}
                    />
                  }
                  label="Automatisch bei Bestellung benachrichtigen"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Test connection button */}
        {enabled && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleTestConnection}
              disabled={!canTest || isTesting}
              startIcon={
                <Icon
                  icon={isTesting ? "mdi:loading" : "mdi:connection"}
                  width="20"
                />
              }
            >
              {isTesting ? "Teste..." : "Verbindung testen"}
            </Button>
            {testResult && (
              <Alert severity={testResult.type} sx={{ mt: 1 }}>
                {testResult.message}
              </Alert>
            )}
          </Box>
        )}

        {/* Feedback */}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Versand-Einstellungen erfolgreich gespeichert.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Submit */}
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            fullWidth
            disabled={isSaving}
            sx={{ mt: 2 }}
          >
            {isSaving ? "Speichert..." : "SPEICHERN"}
          </Button>
        </Box>
      </form>
    </>
  );
};

export default ShopDeliverySettings;
